/*
 * Validation of an uploaded freight cost xlsx file.
 * It reads the rows of the sheets into the freight costs to save,
 * and collects every problem on the way (it does not stop at the first one).
 *
 * There are two entry points, one for each side of the upload:
 *  - validateImportedSheets reads the rows of the file, so it is the one that can point at the
 *    cell a problem belongs to. Only the client has the rows.
 *  - validateImportedFactories checks what the reading gave back, which is what the server
 *    receives. It runs with the factories of the database, since those may have changed since
 *    the file was read on the client.
 *
 * The freight cost types come from the config of the mode,
 * the factory identifiers (shortNames) from the context.
 */

import { FreightCostTypes } from '../../types/freightCost/freightCostType';
import {
  FactoryFreightCosts,
  FreightCostBand,
  FreightCostImportContext,
  FreightCostImportError,
  FreightCostImportErrorType,
  FreightCostImportValidationResult,
  FreightCostsByCostType
} from '../../types/freightCost/freightCostXlsx';
import { XlsxCellValue, XlsxRow, XlsxSheet } from '../../types/xlsx';
import constants from '../constants';
import { getCellReference, isEmptyCell, isEmptyRow, parseCellNumber } from '../xlsx';
import {
  DISTANCE_COLUMN_INDEX,
  getFreightCostTypeHeader,
  getFreightCostTypeHeaders,
  hasDistanceHeader,
  HEADER_ROW_INDEX,
  isOverRangesLabel,
  resolveFreightCostType
} from './freightCostFormat';
import { FreightCostImportErrors } from './freightCostImportErrors';

const { OVER_RANGES_END_DISTANCE } = constants.FREIGHT_COST;

/**
 * Upper limit of a file, counted on its filled rows.
 *
 * This is what holds the request within config.bodyLimit:
 * an xlsx is compressed, so the size of the file says little about how much it holds - the rows do.
 * It is far above any real file, so it only catches a mistaken upload, and with a readable
 * message instead of a request refused for its size.
 */
export const MAX_ROW_COUNT = 5000;

/**
 * Whether a sheet contains no data.
 *
 * @param {XlsxSheet} sheet the sheet to look at
 * @returns {boolean} whether nothing is filled in besides the header of the distance column
 */
function isEmptySheet(sheet: XlsxSheet): boolean {
  return sheet.rows.every((row, rowIndex) =>
    row.every(
      (cell, columnIndex) =>
        isEmptyCell(cell) ||
        // Ignore the DISTANCE_COLUMN_HEADER cell
        (rowIndex === HEADER_ROW_INDEX && columnIndex === DISTANCE_COLUMN_INDEX)
    )
  );
}

/**
 * Whether anything is filled in below the header row of a column.
 *
 * @param {XlsxSheet} sheet the sheet to look at
 * @param {number} columnIdx zero based index of the column
 * @returns {boolean} whether the column holds anything besides its header
 */
function hasColumnData(sheet: XlsxSheet, columnIdx: number): boolean {
  return sheet.rows.some(
    (row, rowIndex) => rowIndex !== HEADER_ROW_INDEX && !isEmptyCell(row[columnIdx])
  );
}

/**
 * Whether anything is filled in beyond the distance column, header or data.
 *
 * @param {XlsxSheet} sheet the sheet to look at
 * @returns {boolean} whether the sheet holds anything outside the distance column
 */
function hasTypeColumns(sheet: XlsxSheet): boolean {
  return sheet.rows.some((row) =>
    row.some((cell, column) => column > DISTANCE_COLUMN_INDEX && !isEmptyCell(cell))
  );
}

/**
 * Resolves the freight cost type of every column of a sheet.
 * Unknown and repeated headers are reported, and so is a column filled in without a header.
 * The number of columns comes from the widest row, not from the header row:
 * a column added by hand without a header leaves the header row shorter than the rows below it.
 *
 * @param {XlsxSheet} sheet the sheet to read
 * @param {FreightCostTypes} freightCostTypes the freight cost types
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {Map<number, string>} the freight cost type of every column
 * that has a valid header, by column index
 */
function collectSheetTypes(
  sheet: XlsxSheet,
  freightCostTypes: FreightCostTypes,
  errors: FreightCostImportErrors
): Map<number, string> {
  const headerRow = sheet.rows[HEADER_ROW_INDEX];
  const columnCount = sheet.rows.reduce((widest, row) => Math.max(widest, row.length), 0);

  const typesByColumn = new Map<number, string>();
  const usedTypes = new Set<string>();

  for (let column = DISTANCE_COLUMN_INDEX + 1; column < columnCount; column++) {
    const cell = getCellReference(HEADER_ROW_INDEX, column);

    if (isEmptyCell(headerRow[column])) {
      if (hasColumnData(sheet, column)) {
        errors.add(FreightCostImportErrorType.emptyTypeHeader, { sheet: sheet.name, cell });
      }
      continue;
    }

    const headerText = String(headerRow[column]).trim();
    const type = resolveFreightCostType(headerText, freightCostTypes);

    if (!type) {
      errors.add(
        FreightCostImportErrorType.unknownTypeHeader,
        { sheet: sheet.name, cell },
        headerText,
        getFreightCostTypeHeaders(freightCostTypes)
      );
    } else if (usedTypes.has(type)) {
      errors.add(
        FreightCostImportErrorType.duplicatedTypeHeader,
        { sheet: sheet.name, cell },
        headerText
      );
    } else {
      usedTypes.add(type);
      typesByColumn.set(column, type);
    }
  }

  return typesByColumn;
}

/**
 * Reads the distance range of a row:
 * a positive whole number, or -1 for the "over the ranges" row.
 *
 * @param {XlsxRow} row the row to read
 * @param {number} rowIndex zero based index of the row
 * @param {string} sheetName name of the sheet, for the error messages
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {number | undefined} the distance range, or undefined when the cell holds no valid one
 */
function readDistance(
  row: XlsxRow,
  rowIndex: number,
  sheetName: string,
  errors: FreightCostImportErrors
): number | undefined {
  const value = row[DISTANCE_COLUMN_INDEX];
  const cell = getCellReference(rowIndex, DISTANCE_COLUMN_INDEX);

  if (isOverRangesLabel(value)) {
    return OVER_RANGES_END_DISTANCE;
  }
  if (isEmptyCell(value)) {
    errors.add(FreightCostImportErrorType.missingDistance, { sheet: sheetName, cell });
    return undefined;
  }

  const distance = parseCellNumber(value);
  if (distance === undefined || !Number.isInteger(distance) || distance <= 0) {
    errors.add(
      FreightCostImportErrorType.invalidDistance,
      { sheet: sheetName, cell },
      String(value).trim()
    );
    return undefined;
  }

  return distance;
}

/**
 * Reads the freight cost of a cell:
 * a non-negative whole number.
 *
 * @param {XlsxCellValue} value the value of the cell
 * @param {string} cell reference of the cell, for the error message
 * @param {string} sheetName name of the sheet, for the error message
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {number | undefined} the freight cost,
 * or undefined when the cell is empty or holds no valid cost
 */
function readCost(
  value: XlsxCellValue,
  cell: string,
  sheetName: string,
  errors: FreightCostImportErrors
): number | undefined {
  if (isEmptyCell(value)) {
    return undefined;
  }

  const cost = parseCellNumber(value);
  if (cost === undefined || !Number.isInteger(cost) || cost < 0) {
    errors.add(
      FreightCostImportErrorType.invalidCost,
      { sheet: sheetName, cell },
      String(value).trim()
    );
    return undefined;
  }

  return cost;
}

/**
 * Reads the distance ranges of a sheet, checking the column of the distances as a whole:
 * every value is a positive whole number (or the "over the ranges" row)
 * and none of them is repeated.
 * Their order does not matter, the bands are stored by their distance.
 * A row whose distance was reported as a problem is left out,
 * so a type never gets two bands for the same distance.
 *
 * @param {XlsxSheet} sheet the sheet to read
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {Map<number, number>} the distance range of every row that holds a valid one,
 * by row index
 */
function readSheetDistances(
  sheet: XlsxSheet,
  errors: FreightCostImportErrors
): Map<number, number> {
  const distanceByRow = new Map<number, number>();
  const usedDistances = new Set<number>();

  for (let rowIndex = HEADER_ROW_INDEX + 1; rowIndex < sheet.rows.length; rowIndex++) {
    const row = sheet.rows[rowIndex];
    if (isEmptyRow(row)) {
      continue;
    }

    const distance = readDistance(row, rowIndex, sheet.name, errors);
    if (distance === undefined) {
      continue;
    }
    const cell = getCellReference(rowIndex, DISTANCE_COLUMN_INDEX);

    if (usedDistances.has(distance)) {
      // the "over the ranges" row is a distance range as well, only it has no number to show
      errors.add(
        FreightCostImportErrorType.duplicatedDistance,
        { sheet: sheet.name, cell },
        distance === OVER_RANGES_END_DISTANCE ? undefined : distance
      );
      continue;
    }
    usedDistances.add(distance);

    distanceByRow.set(rowIndex, distance);
  }

  return distanceByRow;
}

/**
 * Reads the bands of one freight cost type:
 * the filled cells of its column, each paired with the distance range of its row.
 * Reports the column when nothing is filled in it at all - a column holding unreadable
 * values is reported cell by cell instead.
 *
 * @param {XlsxSheet} sheet the sheet to read
 * @param {number} column zero based index of the column of the type
 * @param {Map<number, number>} distanceByRow the distance range of every row
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {FreightCostBand[]} the bands of the type, empty when none of them can be read
 */
function readTypeCosts(
  sheet: XlsxSheet,
  column: number,
  distanceByRow: Map<number, number>,
  errors: FreightCostImportErrors
): FreightCostBand[] {
  const costs: FreightCostBand[] = [];
  let hasFilledCell = false;

  for (let rowIndex = HEADER_ROW_INDEX + 1; rowIndex < sheet.rows.length; rowIndex++) {
    const value = sheet.rows[rowIndex][column];
    if (isEmptyCell(value)) {
      continue;
    }
    hasFilledCell = true;

    const cost = readCost(value, getCellReference(rowIndex, column), sheet.name, errors);
    const distance = distanceByRow.get(rowIndex);
    if (cost !== undefined && distance !== undefined) {
      costs.push({ endDistance: distance, freightCost: cost });
    }
  }

  if (!hasFilledCell) {
    errors.add(FreightCostImportErrorType.emptyTypeColumn, {
      sheet: sheet.name,
      cell: getCellReference(HEADER_ROW_INDEX, column)
    });
  }

  return costs;
}

/**
 * Reads the freight costs of one sheet, type by type:
 * first the column of the distances, then the column of every type.
 * A sheet without data is not a problem: the factory of an empty
 * sheet is simply left untouched.
 *
 * @param {XlsxSheet} sheet the sheet to read
 * @param {FreightCostTypes} freightCostTypes the freight cost types
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {FreightCostsByCostType[]} the types filled in on the sheet,
 * empty when the sheet holds no readable data
 */
function readSheetTypes(
  sheet: XlsxSheet,
  freightCostTypes: FreightCostTypes,
  errors: FreightCostImportErrors
): FreightCostsByCostType[] {
  if (isEmptySheet(sheet)) {
    return [];
  }

  const headerRow = sheet.rows[HEADER_ROW_INDEX];
  if (!hasDistanceHeader(headerRow)) {
    errors.add(FreightCostImportErrorType.missingDistanceHeader, {
      sheet: sheet.name,
      cell: getCellReference(HEADER_ROW_INDEX, DISTANCE_COLUMN_INDEX)
    });
    return [];
  }

  const typesByColumn = collectSheetTypes(sheet, freightCostTypes, errors);
  if (!typesByColumn.size) {
    /*
     * Unknown headers, duplicated headers and columns filled in without a header are reported
     * on their own cells already.
     */
    if (!hasTypeColumns(sheet)) {
      errors.add(FreightCostImportErrorType.missingTypeColumns, { sheet: sheet.name });
    }
    return [];
  }

  const distanceByRow = readSheetDistances(sheet, errors);
  const types: FreightCostsByCostType[] = [];

  for (const [column, type] of typesByColumn) {
    const costs = readTypeCosts(sheet, column, distanceByRow, errors);
    if (costs.length) {
      types.push({ type, costs });
    }
  }

  return types;
}

/**
 * Resolves the factory of a sheet by its name. The identifier is matched as it is stored
 * (uppercase), only the surrounding spaces are ignored.
 *
 * @param {XlsxSheet} sheet the sheet to resolve
 * @param {Set<string>} usedFactoryIds the factories resolved so far,
 * extended with the returned one
 * @param {FreightCostImportContext} context the factory identifiers to resolve with
 * @param {FreightCostImportErrors} errors gathers the problems found
 * @returns {string | undefined} the identifier (shortName) of the factory,
 * or undefined when it cannot be resolved
 */
function resolveSheetFactory(
  sheet: XlsxSheet,
  usedFactoryIds: Set<string>,
  context: FreightCostImportContext,
  errors: FreightCostImportErrors
): string | undefined {
  const factoryId = sheet.name.trim();

  if (!context.factoryShortNames.includes(factoryId)) {
    errors.add(FreightCostImportErrorType.unknownFactory, { sheet: sheet.name });
    return undefined;
  }
  if (usedFactoryIds.has(factoryId)) {
    errors.add(FreightCostImportErrorType.duplicatedFactory, { sheet: sheet.name }, factoryId);
    return undefined;
  }
  usedFactoryIds.add(factoryId);

  return factoryId;
}

/**
 * Validates an uploaded freight cost xlsx file and reads the freight costs to save out of it.
 *
 * @param {XlsxSheet[]} sheets of the file, as the parser read them:
 * one sheet per factory, where the first row is the header
 * @param {FreightCostTypes} freightCostTypes the freight cost types (config.freightCostTypes)
 * @param {FreightCostImportContext} context the factory identifiers to resolve the sheet names
 * with
 * @returns {FreightCostImportValidationResult} every problem found, and the freight costs to
 * save when there is none - with no error there is at least one factory to save
 */
export function validateImportedSheets(
  sheets: XlsxSheet[],
  freightCostTypes: FreightCostTypes,
  context: FreightCostImportContext
): FreightCostImportValidationResult {
  const errors = new FreightCostImportErrors();
  const factories: FactoryFreightCosts[] = [];
  const result: FreightCostImportValidationResult = { errors: errors.list, factories };

  if (!sheets.length) {
    errors.add(FreightCostImportErrorType.emptyFile, {});
    return result;
  }

  const rowCount = sheets.reduce(
    (count, sheet) => count + sheet.rows.filter((row) => !isEmptyRow(row)).length,
    0
  );
  if (rowCount > MAX_ROW_COUNT) {
    errors.add(FreightCostImportErrorType.tooManyRows, {}, rowCount, MAX_ROW_COUNT);
    return result;
  }

  const usedFactoryIds = new Set<string>();

  for (const sheet of sheets) {
    const factoryId = resolveSheetFactory(sheet, usedFactoryIds, context, errors);
    if (!factoryId) {
      continue;
    }

    const types = readSheetTypes(sheet, freightCostTypes, errors);
    if (types.length) {
      factories.push({ factoryId, types });
    }
  }

  /*
    Every sheet was left empty, so there is nothing to save. Only worth reporting when the file
    is faultless otherwise: an error above means the data is there, just not readable.
  */
  if (!factories.length && !errors.list.length) {
    errors.add(FreightCostImportErrorType.nothingToImport, {});
  }

  return result;
}

/**
 * Checks the bands of one freight cost type:
 * every distance range is a positive whole number (or the "over the ranges" one),
 * none of them is repeated,
 * and every cost is a whole non-negative number.
 *
 * @param {FreightCostBand[]} costs the bands of the type
 * @param {string} sheetName name of the sheet the type belongs to, for the error messages
 * @param {FreightCostImportErrors} errors gathers the problems found
 */
function validateTypeCosts(
  costs: FreightCostBand[],
  sheetName: string,
  errors: FreightCostImportErrors
): void {
  if (!costs.length) {
    errors.add(FreightCostImportErrorType.emptyTypeColumn, { sheet: sheetName });
    return;
  }

  const usedDistances = new Set<number>();

  for (const { endDistance, freightCost } of costs) {
    const isValidDistance =
      endDistance === OVER_RANGES_END_DISTANCE ||
      (Number.isInteger(endDistance) && endDistance > 0);

    if (!isValidDistance) {
      errors.add(
        FreightCostImportErrorType.invalidDistance,
        { sheet: sheetName },
        String(endDistance)
      );
      continue;
    }
    if (usedDistances.has(endDistance)) {
      errors.add(
        FreightCostImportErrorType.duplicatedDistance,
        { sheet: sheetName },
        endDistance === OVER_RANGES_END_DISTANCE ? undefined : endDistance
      );
      continue;
    }
    usedDistances.add(endDistance);

    if (!Number.isInteger(freightCost) || freightCost < 0) {
      errors.add(FreightCostImportErrorType.invalidCost, { sheet: sheetName }, String(freightCost));
    }
  }
}

/**
 * Checks the freight cost types of one factory:
 * every type exists, none of them is repeated, and each of them holds readable bands.
 *
 * @param {FreightCostsByCostType[]} types the types read for the factory
 * @param {string} sheetName name of the sheet of the factory, for the error messages
 * @param {FreightCostTypes} freightCostTypes the freight cost types
 * @param {FreightCostImportErrors} errors gathers the problems found
 */
function validateFactoryTypes(
  types: FreightCostsByCostType[],
  sheetName: string,
  freightCostTypes: FreightCostTypes,
  errors: FreightCostImportErrors
): void {
  if (!types.length) {
    errors.add(FreightCostImportErrorType.missingTypeColumns, { sheet: sheetName });
    return;
  }

  const usedTypes = new Set<string>();

  for (const { type, costs } of types) {
    if (!Object.keys(freightCostTypes).includes(type)) {
      errors.add(
        FreightCostImportErrorType.unknownTypeHeader,
        { sheet: sheetName },
        getFreightCostTypeHeader(freightCostTypes, type),
        getFreightCostTypeHeaders(freightCostTypes)
      );
      continue;
    }
    if (usedTypes.has(type)) {
      errors.add(
        FreightCostImportErrorType.duplicatedTypeHeader,
        { sheet: sheetName },
        getFreightCostTypeHeader(freightCostTypes, type)
      );
      continue;
    }
    usedTypes.add(type);

    validateTypeCosts(costs, sheetName, errors);
  }
}

/**
 * Checks the freight costs read from an uploaded file against the stored data.
 * Since there are no cells to point at, every problem is reported on the factory it belongs to.
 *
 * @param {FactoryFreightCosts[]} factories the freight costs to save, factory by factory
 * @param {FreightCostTypes} freightCostTypes the freight cost types
 * @param {FreightCostImportContext} context the factory identifiers to accept
 * @returns {FreightCostImportError[]} every problem found, empty when there is none
 */
export function validateImportedFactories(
  factories: FactoryFreightCosts[],
  freightCostTypes: FreightCostTypes,
  context: FreightCostImportContext
): FreightCostImportError[] {
  const errors = new FreightCostImportErrors();

  if (!factories.length) {
    errors.add(FreightCostImportErrorType.nothingToImport, {});
    return errors.list;
  }
  const usedFactoryIds = new Set<string>();

  for (const { factoryId, types } of factories) {
    if (!context.factoryShortNames.includes(factoryId)) {
      errors.add(FreightCostImportErrorType.unknownFactory, { sheet: factoryId });
      continue;
    }
    if (usedFactoryIds.has(factoryId)) {
      errors.add(FreightCostImportErrorType.duplicatedFactory, { sheet: factoryId }, factoryId);
      continue;
    }
    usedFactoryIds.add(factoryId);

    validateFactoryTypes(types, factoryId, freightCostTypes, errors);
  }

  return errors.list;
}
